import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WifiIcon from "@mui/icons-material/Wifi";
import BusinessIcon from "@mui/icons-material/Business";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { fetchIpGeoInfo } from "./ipGeoService";

// Country flag emoji helper
const getFlagEmoji = (countryCode) => {
    if (!countryCode || countryCode.length !== 2) return "🌐";
    return countryCode
        .toUpperCase()
        .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));
};

const Badge = ({ label, color, bg }) => (
    <Box
        sx={{
            display: "inline-flex",
            alignItems: "center",
            px: "8px",
            py: "2px",
            borderRadius: "20px",
            backgroundColor: bg,
            border: `1px solid ${color}`,
        }}
    >
        <Typography sx={{ fontSize: "11px", fontFamily: "Sofia Pro Bold, sans-serif", color }}>
            {label}
        </Typography>
    </Box>
);

const InfoRow = ({ icon, label, value, isLink, href, valueColor }) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: "12px",
            px: "16px",
            borderBottom: "1px solid #EEEEEE",
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box sx={{ color: "#BBBBBB", display: "flex", alignItems: "center" }}>{icon}</Box>
            <Typography
                sx={{ fontSize: "13px", fontFamily: "Sofia Pro Bold, sans-serif", color: "#1D1D1B" }}
            >
                {label}
            </Typography>
        </Box>
        {isLink ? (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    fontSize: "13px",
                    color: "#007bff",
                    textDecoration: "underline",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                }}
            >
                {value}
                <OpenInNewIcon sx={{ fontSize: "13px" }} />
            </a>
        ) : (
            <Typography
                sx={{
                    fontSize: "13px",
                    fontFamily: "Sofia Pro Bold, sans-serif",
                    color: valueColor || "#1D1D1B",
                    textAlign: "right",
                    maxWidth: "55%",
                    wordBreak: "break-word",
                }}
            >
                {value || "—"}
            </Typography>
        )}
    </Box>
);

const SkeletonRow = () => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: "14px",
            px: "16px",
            borderBottom: "1px solid #EEEEEE",
        }}
    >
        <Box
            sx={{ height: "12px", width: "35%", borderRadius: "6px", backgroundColor: "#F0F0F0", animation: "pulse 1.4s ease-in-out infinite" }}
        />
        <Box
            sx={{ height: "12px", width: "40%", borderRadius: "6px", backgroundColor: "#F0F0F0", animation: "pulse 1.4s ease-in-out infinite" }}
        />
    </Box>
);

const IPGeoView = ({ ip }) => {
    const [geoData, setGeoData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadGeoData = useCallback(async () => {
        if (!ip) return;
        setLoading(true);
        setError(null);
        setGeoData(null);
        try {
            const data = await fetchIpGeoInfo(ip);
            setGeoData(data);
        } catch (err) {
            setError(err.message || "Failed to fetch IP information.");
        } finally {
            setLoading(false);
        }
    }, [ip]);

    useEffect(() => {
        loadGeoData();
    }, [loadGeoData]);

    const mapUrl =
        geoData?.lat && geoData?.lon
            ? `https://www.google.com/maps?q=${geoData.lat},${geoData.lon}`
            : null;

    return (
        <Box sx={{ pb: 4 }}>
            {/* Header card */}
            <Box
                sx={{
                    mx: 2,
                    mt: 2,
                    mb: 1,
                    p: 2,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #1D1D1B 0%, #3a3a38 100%)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <PublicIcon sx={{ color: "#fff", fontSize: "28px" }} />
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "Sofia Pro Bold, sans-serif",
                            fontSize: "16px",
                            color: "#fff",
                            letterSpacing: "0.5px",
                        }}
                    >
                        {ip}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#BBBBBB", fontFamily: "Sofia Pro, sans-serif" }}>
                        IP Geolocation Lookup
                    </Typography>
                </Box>
            </Box>

            {/* Loading state */}
            {loading && (
                <Box sx={{ backgroundColor: "#fff", mx: 2, borderRadius: "10px", border: "1px solid #EEEEEE", overflow: "hidden" }}>
                    {[...Array(8)].map((_, i) => (
                        <SkeletonRow key={i} />
                    ))}
                    <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.4; }
            }
          `}</style>
                </Box>
            )}

            {/* Error state */}
            {error && !loading && (
                <Box
                    sx={{
                        mx: 2,
                        mt: 1,
                        p: 3,
                        borderRadius: "10px",
                        border: "1px solid #FFE0B2",
                        backgroundColor: "#FFF8F0",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <WarningAmberIcon sx={{ color: "#FF9800", fontSize: "32px" }} />
                    <Typography
                        sx={{
                            fontFamily: "Sofia Pro Bold, sans-serif",
                            fontSize: "13px",
                            color: "#E65100",
                            textAlign: "center",
                        }}
                    >
                        {error}
                    </Typography>
                    <button
                        onClick={loadGeoData}
                        style={{
                            border: "1px solid #1D1D1B",
                            borderRadius: "8px",
                            padding: "6px 18px",
                            fontSize: "13px",
                            fontFamily: "Sofia Pro Bold, sans-serif",
                            backgroundColor: "#1D1D1B",
                            color: "#fff",
                            cursor: "pointer",
                        }}
                    >
                        Retry
                    </button>
                </Box>
            )}

            {/* Geo info */}
            {geoData && !loading && (
                <>
                    {/* Country hero */}
                    <Box
                        sx={{
                            mx: 2,
                            mt: 1,
                            p: "14px 16px",
                            borderRadius: "10px",
                            border: "1px solid #EEEEEE",
                            backgroundColor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontSize: "28px", lineHeight: 1 }}>
                                {getFlagEmoji(geoData.countryCode)}
                            </Typography>
                            <Box>
                                <Typography
                                    sx={{ fontFamily: "Sofia Pro Bold, sans-serif", fontSize: "15px", color: "#1D1D1B" }}
                                >
                                    {geoData.country || "Unknown Country"}
                                </Typography>
                                <Typography
                                    sx={{ fontFamily: "Sofia Pro, sans-serif", fontSize: "12px", color: "#BBBBBB" }}
                                >
                                    {geoData.countryCode}
                                </Typography>
                            </Box>
                        </Box>
                        {/* Flag badges */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-end" }}>
                            {geoData.proxy === true && (
                                <Badge label="Proxy / VPN" color="#E65100" bg="#FFF3E0" />
                            )}
                            {geoData.hosting === true && (
                                <Badge label="Datacenter" color="#6A1B9A" bg="#F3E5F5" />
                            )}
                            {geoData.mobile === true && (
                                <Badge label="Mobile" color="#1565C0" bg="#E3F2FD" />
                            )}
                            {geoData.proxy === false && geoData.hosting === false && (
                                <Badge label="Residential" color="#2E7D32" bg="#E8F5E9" />
                            )}
                        </Box>
                    </Box>

                    {/* Detail rows */}
                    <Box
                        sx={{
                            mx: 2,
                            mt: 1,
                            borderRadius: "10px",
                            border: "1px solid #EEEEEE",
                            backgroundColor: "#fff",
                            overflow: "hidden",
                        }}
                    >
                        <InfoRow
                            icon={<LocationOnIcon sx={{ fontSize: "16px" }} />}
                            label="City"
                            value={[geoData.city, geoData.region].filter(Boolean).join(", ")}
                        />
                        <InfoRow
                            icon={<AccessTimeIcon sx={{ fontSize: "16px" }} />}
                            label="Timezone"
                            value={geoData.timezone}
                        />
                        <InfoRow
                            icon={<WifiIcon sx={{ fontSize: "16px" }} />}
                            label="ISP"
                            value={geoData.isp}
                        />
                        <InfoRow
                            icon={<BusinessIcon sx={{ fontSize: "16px" }} />}
                            label="Organisation"
                            value={geoData.org !== geoData.isp ? geoData.org : null}
                        />
                        <InfoRow
                            icon={<PublicIcon sx={{ fontSize: "16px" }} />}
                            label="ASN"
                            value={geoData.asn}
                        />
                        {mapUrl && (
                            <InfoRow
                                icon={<LocationOnIcon sx={{ fontSize: "16px" }} />}
                                label="View on Map"
                                value="Open in Google Maps"
                                isLink
                                href={mapUrl}
                            />
                        )}
                    </Box>

                    {/* Source attribution */}
                    <Typography
                        sx={{
                            fontSize: "11px",
                            color: "#BBBBBB",
                            textAlign: "center",
                            mt: 1.5,
                            fontFamily: "Sofia Pro, sans-serif",
                        }}
                    >
                        Data via {geoData.source} · Cached for this session
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default IPGeoView;
